<template>
  <div class="relative" :class="'field' + name">
    <Field
        v-model="internalValue"
        :name="name"
        :rules="rules"
        class="w-full border rounded-small shadow-gray pt-4 pb-[13px] px-[18px] text-base sm:text-xl border-charcoal placeholder:text-silver h-[49px] sm:h-[53px]"
        :class="{ 'border-red-500': hasSubscriptionError }"
        :placeholder="placeholder"
        @focus="focusField"
        @blur="focusField"
    />
    <span
        :class="isFocused ? 'opacity-100 z-[1]' : ' opacity-0 z-0'"
        class="absolute bg-white -top-2 left-4 px-1 transition-all duration-75 ease-linear text-sm"
    >{{ placeholder }}</span>
    
    <!-- Real-time validation error display -->
    <div v-if="subscriptionError" class="text-red-500 text-sm mt-2 leading-tight">
      {{ subscriptionError }}
    </div>
    
    <!-- Loading indicator -->
    <div v-if="isValidating" class="text-gray-500 text-sm mt-1">
      Validating postal code...
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { Field, useField } from 'vee-validate'
import { useDebounceFn } from '@vueuse/core'
import { fetchData } from '@/api/query'

export default defineComponent({
  components: { Field },
  props: {
    name: {
      type: String,
      required: true
    },
    rules: {
      type: Object,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'validation-error', 'validation-success'],
  setup(props, { emit }) {
    const { errorMessage, setErrors } = useField(props.name, props.rules)
    const subscriptionError = ref('')
    const isValidating = ref(false)
    const hasSubscriptionError = ref(false)
    
    const internalValue = computed({
      get: () => props.modelValue,
      set: (val) => {
        debouncedValidation(val)
        emit('update:modelValue', val)
      }
    })

    const isFocused = ref(false)

    function focusField() {
      isFocused.value = !isFocused.value
    }

    const validateZipCode = async (zipCode: string) => {
      if (!zipCode || zipCode.trim().length === 0) {
        subscriptionError.value = ''
        hasSubscriptionError.value = false
        emit('validation-success')
        return
      }

      const trimmedZip = zipCode.trim()
      
      // First check if it's a valid US ZIP format (5 digits)
      if (!/^\d{5}$/.test(trimmedZip)) {
        // Check if it looks like an international postal code
        const isUKPostcode = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(trimmedZip)
        const isCanadianPostcode = /^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/i.test(trimmedZip)
        
        if (isUKPostcode || isCanadianPostcode) {
          subscriptionError.value = 'International shipping is only available for subscriptions. Please visit our signup page to place an order to this location.'
          hasSubscriptionError.value = true
          emit('validation-error', subscriptionError.value)
          return
        } else if (trimmedZip.length > 0) {
          subscriptionError.value = 'Please enter a valid US ZIP code (5 digits)'
          hasSubscriptionError.value = true
          emit('validation-error', subscriptionError.value)
          return
        }
      }

      // If it's a valid US ZIP format, check with the API to be sure
      if (/^\d{5}$/.test(trimmedZip)) {
        isValidating.value = true
        
        try {
          const response: any = await fetchData(`zip/${trimmedZip}`, 'get')
          
          if (response?.zone?.subscription_only) {
            subscriptionError.value = 'International shipping is only available for subscriptions. Please visit our signup page to place an order to this location.'
            hasSubscriptionError.value = true
            emit('validation-error', subscriptionError.value)
          } else {
            // Valid US ZIP code
            subscriptionError.value = ''
            hasSubscriptionError.value = false
            emit('validation-success')
          }
        } catch (error: any) {
          // If API call fails, assume it's an invalid ZIP
          subscriptionError.value = 'Invalid ZIP code'
          hasSubscriptionError.value = true
          emit('validation-error', subscriptionError.value)
        } finally {
          isValidating.value = false
        }
      }
    }

    const debouncedValidation = useDebounceFn(async (val: string) => {
      await validateZipCode(val)
    }, 500)

    // Watch for external changes to modelValue
    watch(
        () => props.modelValue,
        (newValue) => {
          if (newValue !== internalValue.value) {
            internalValue.value = newValue
          }
        }
    )

    return {
      internalValue,
      isFocused,
      focusField,
      errorMessage,
      subscriptionError,
      isValidating,
      hasSubscriptionError
    }
  }
})
</script>
